import { useRef } from 'react'
import useAppStore from '$appStore'
import { CompanySettings } from '$models'
import { constructActiveCompanySettings } from '$utils'

export function useCompanySettings(): CompanySettings {
  const activeCompany = useAppStore((state) => state.activeCompany)
  const cacheRef = useRef<{
    activeCompany: typeof activeCompany
    settings: CompanySettings
  }>()

  if (cacheRef.current?.activeCompany !== activeCompany) {
    if (activeCompany) {
      const settings = constructActiveCompanySettings(activeCompany)

      cacheRef.current = {
        activeCompany,
        settings: {
          address: activeCompany.address,
          name: activeCompany.name,
          tier: activeCompany.tier,
          pos: activeCompany.pos,
          invoiceEmail: activeCompany.invoiceEmail,
          countryPreference: settings.countryPreference,
          weekStartPreference: settings.weekStartPreference,
          dayStartPreference: settings.dayStartPreference,
          localePreference: settings.localePreference,
          timeZonePreference: settings.timeZonePreference,
          provincePreference: settings.provincePreference ?? undefined,
          payrollSystemPreference: settings.payrollSystemPreference,
          hasPayrollPermission: settings.hasPayrollPermission,
          hasManagerPermission: settings.hasManagerPermission,
          hasTimePermission: settings.hasTimePermission,
          hasSalaryPermission: settings.hasSalaryPermission,
          hasStockPermission: settings.hasStockPermission,
          defaultFreightTaxRatePreference:
            settings.defaultFreightTaxRatePreference,
          overrideSalesTaxRatePreference:
            settings.overrideSalesTaxRatePreference,
          includePosClockinsPreference: settings.includePosClockinsPreference,
          timeAllowsPaySummaryPreference:
            settings.timeAllowsPaySummaryPreference,
          clockinRoundingPreference: settings.clockinRoundingPreference,
          breakRoundingTargetPreference: settings.breakRoundingTargetPreference,
          breakRoundingMinimumPreference:
            settings.breakRoundingMinimumPreference,
          timeRosterPublicTokenPreference:
            settings.timeRosterPublicTokenPreference,
          xeroFreightAccountPreference: settings.xeroFreightAccountPreference,
          teamAppEnabled: settings.teamAppEnabled,
          teamAppDisplayPayInformation: settings.teamAppDisplayPayInformation,
          teamAppDisplayPhoneNumbers: settings.teamAppDisplayPhoneNumbers,
          teamAppShiftSwapRequireManagerApproval:
            settings.teamAppShiftSwapRequireManagerApproval,
          teamAppShiftSwapAllowOfferToEveryone:
            settings.teamAppShiftSwapAllowOfferToEveryone,
          teamAppShiftSwapTransferCutoffMinutes:
            settings.teamAppShiftSwapTransferCutoffMinutes,
          teamAppShiftSwapRestrictTransfers:
            settings.teamAppShiftSwapRestrictTransfers,
          teamAppShiftSwapRestrictTransfersBy:
            settings.teamAppShiftSwapRestrictTransfersBy,
        },
      }
    } else {
      cacheRef.current = { activeCompany, settings: {} as CompanySettings }
    }
  }

  return cacheRef.current.settings
}
